import type { ReactNode } from 'react'
import { ServerMetaHead } from '@/components/shared/ServerMetaHead'
import { ContactWidget } from '@/components/shared/ContactWidget'
import { ExitIntentNewsletter } from '@/components/shared/ExitIntentNewsletter'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { CTAButton } from '@/components/layout/CTAButton'
import { RebateBanner } from '@/components/layout/RebateBanner'

interface LayoutProps {
  children: ReactNode
  title?: string
}

/**
 * Always-visible mobile booking affordance. The Header CTA is `hidden sm:inline-flex`
 * and the in-sheet booking link requires opening the hamburger first, so on small
 * screens there was no persistent way to book. This fixed bottom bar (md:hidden)
 * keeps "Book a free test ride" one tap away on every page. `<main>` gets matching
 * bottom padding (pb-20 md:pb-0) below so the bar never overlaps footer content;
 * the bar itself adds env(safe-area-inset-bottom) so it clears the iOS home bar.
 */
function MobileBookingBar() {
  return (
    <div
      data-testid="mobile-booking-bar"
      className="fixed inset-x-0 bottom-0 z-50 md:hidden border-t border-border bg-background/95 backdrop-blur-[10px] supports-[backdrop-filter]:bg-background/85 px-4 pt-3"
      style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
    >
      <CTAButton href="/book" size="lg" className="w-full">
        Book a free test ride
      </CTAButton>
    </div>
  )
}

export default function Layout({ children }: LayoutProps) {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground antialiased">
      <ServerMetaHead />
      {/* Announcement bar sits above the Header and self-hides outside the
          state's published registration windows. See RebateBanner. */}
      <RebateBanner />
      <Header />
      {/* pb-20 md:pb-0 reserves room for the fixed MobileBookingBar so it can't
          overlap the footer on mobile; removed at md where the bar is hidden. */}
      <main className="flex-1 pb-20 md:pb-0">{children}</main>
      <Footer />
      <ExitIntentNewsletter />
      <ContactWidget aboveMobileBar />
      <MobileBookingBar />
    </div>
  )
}
